import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Device } from '@capacitor/device';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { Role } from 'src/app/enums';
import { User, LogIn } from 'src/app/interfaces';

import {
    ActivateLandingPage,
    GetPublicToken,
    HideAuthWidget,
    Login,
    Logout,
    Register,
    SyncAllProducts,
    SyncAllUpdates,
    SyncLandingPage
} from './auth.actions';
import { AuthState } from './auth.state';

@Injectable({
    providedIn: 'root'
})
export class AuthFacade {
    helper!: JwtHelperService;
    displayAuthWidget$ = this.store.select(AuthState.displayAuthWidget);
    syncingAll$ = this.store.select(AuthState.syncingAll);
    syncingUpdates$ = this.store.select(AuthState.syncingUpdates);
    syncingLandingPage$ = this.store.select(AuthState.syncingLandingPage);
    activatingLandingPage$ = this.store.select(AuthState.activatingLandingPage);
    private publicTokenRequest: Promise<void> | null = null;

    constructor(private store: Store) {
        this.helper = new JwtHelperService();
    }

    get isAuth(): boolean {
        const token = this.store.selectSnapshot(AuthState.token);
        if (token) {
            return true;
        }

        return false;
    }

    get user(): User | null {
        const token = this.store.selectSnapshot(AuthState.token);
        if (token) {
            return this.helper.decodeToken(token).user as User;
        }
        return null;
    }

    get isAdmin(): boolean {
        if (this.isAuth) {
            return this.user?.roles?.includes(Role.Admin);
        }
        return false;
    }

    login(payload: LogIn) {
        return this.store.dispatch(new Login(payload));
    }

    register(payload: User) {
        return this.store.dispatch(new Register(payload));
    }

    ensurePublicToken(): Promise<void> {
        if (this.store.selectSnapshot(AuthState.publicToken)) {
            return Promise.resolve();
        }

        if (!this.publicTokenRequest) {
            this.publicTokenRequest = Device.getId()
                .then(({ identifier }) => lastValueFrom(this.store.dispatch(new GetPublicToken(identifier))))
                .then(() => undefined)
                .finally(() => {
                    this.publicTokenRequest = null;
                });
        }

        return this.publicTokenRequest;
    }

    logout() {
        return this.store.dispatch(new Logout());
    }
    hideAuthWidget() {
        return this.store.dispatch(new HideAuthWidget());
    }
    syncAll() {
        return this.store.dispatch(new SyncAllProducts());
    }
    syncUpdates() {
        return this.store.dispatch(new SyncAllUpdates());
    }
    syncLandingPage() {
        return this.store.dispatch(new SyncLandingPage());
    }
    activateLandingPage() {
        return this.store.dispatch(new ActivateLandingPage());
    }
}
